import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Info, Package, Pencil, Upload } from 'lucide-react';
import {
  supplyAgentService,
  type SupplyAgentLogisticsCalculateResponse,
  type SupplyAgentLogisticsMasterStatus,
  type SupplyAgentLogisticsUploadResponse,
  type SupplyAgentSimulateResponse,
} from '@/services/supplyAgentService';
import { logisticsResultToAgentSimulation } from '@/modules/supply/components/logisticsToAgentSimulation';
import { SupplyAgentLogisticsMasterEditor } from '@/modules/supply/components/SupplyAgentLogisticsMasterEditor';
import {
  localizeLogisticsTechnicalText,
  logisticsRuleSummary,
  logisticsStatusLabel,
  logisticsStrategyLabel,
  logisticsZoneLabel,
} from '@/modules/supply/components/logisticsBusinessLabels';

interface SupplyAgentLogisticsPanelProps {
  supplierId: number | null;
  warehouseId: number;
  hideTitle?: boolean;
  /** Oculta «Aplicar al pedido sugerido» (p. ej. módulo Reglas sin bandeja). */
  hideApplyToOrder?: boolean;
  onApplyToSuggestedOrder?: (simulation: SupplyAgentSimulateResponse) => void;
}

function formatNum(value: number | undefined | null, digits = 2): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

function formatUpdatedAt(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function SupplyAgentLogisticsPanel({
  supplierId,
  warehouseId,
  hideTitle = false,
  hideApplyToOrder = false,
  onApplyToSuggestedOrder,
}: SupplyAgentLogisticsPanelProps) {
  const { t, i18n } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadOkMessage, setUploadOkMessage] = useState<string | null>(null);
  const [applyOkMessage, setApplyOkMessage] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<SupplyAgentLogisticsUploadResponse | null>(null);
  const [calcResult, setCalcResult] = useState<SupplyAgentLogisticsCalculateResponse | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [masterStatus, setMasterStatus] = useState<SupplyAgentLogisticsMasterStatus | null>(null);
  const [masterLoading, setMasterLoading] = useState(false);
  const [showMasterEditor, setShowMasterEditor] = useState(false);

  const canSimulate = supplierId != null && warehouseId > 0;
  const canApply =
    canSimulate &&
    calcResult != null &&
    (calcResult.status === 'SUCCESS' || calcResult.status === 'SUCCESS_WITH_WARNINGS') &&
    (calcResult.line_adjustments?.length || 0) > 0;

  const isContainerMode =
    Boolean(calcResult?.container) ||
    String(calcResult?.rule?.constraint_type || '').toUpperCase() === 'MIN_CONTAINER_FILL';

  const totalDeltaUnits = (calcResult?.line_adjustments || []).reduce(
    (sum, row) => sum + Number(row.delta_units || 0),
    0,
  );

  const ruleSummaryText = calcResult?.rule
    ? logisticsRuleSummary(t, calcResult.rule as Record<string, unknown>)
    : '';

  const refreshMasterStatus = async (sid: number | null) => {
    if (sid == null) {
      setMasterStatus(null);
      return;
    }
    setMasterLoading(true);
    try {
      const data = await supplyAgentService.logisticsMasterStatus({ supplier_id: sid });
      setMasterStatus(data);
    } catch {
      setMasterStatus(null);
    } finally {
      setMasterLoading(false);
    }
  };

  useEffect(() => {
    void refreshMasterStatus(supplierId);
    setShowMasterEditor(false);
  }, [supplierId]);

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setUploadOkMessage(null);
    try {
      const data = await supplyAgentService.uploadLogisticsExcel(file, {
        replace_existing: replaceExisting,
      });
      setUploadResult(data);
      setCalcResult(null);
      const created =
        (data.products?.created || 0) +
        (data.rules?.created || 0) +
        (data.containers?.created || 0);
      const updated =
        (data.products?.updated || 0) +
        (data.rules?.updated || 0) +
        (data.containers?.updated || 0);
      const errs =
        (data.products?.errors?.length || 0) +
        (data.rules?.errors?.length || 0) +
        (data.containers?.errors?.length || 0);
      setUploadOkMessage(
        errs > 0
          ? t('supply.supplyAgent.logisticsUploadDoneWithErrors', {
              created,
              updated,
              errors: errs,
            })
          : t('supply.supplyAgent.logisticsUploadDoneOk', { created, updated }),
      );
      await refreshMasterStatus(supplierId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.logisticsUploadError'));
      setUploadOkMessage(null);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const runSimulate = async () => {
    if (!canSimulate || supplierId == null) return;
    setSimulating(true);
    setError(null);
    setApplyOkMessage(null);
    try {
      const data = await supplyAgentService.logisticsCalculate({
        mode: 'SIMULATE',
        supplier_id: supplierId,
        warehouse_id: warehouseId,
        load_suggested_order: true,
      });
      setCalcResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.logisticsSimulateError'));
    } finally {
      setSimulating(false);
    }
  };

  const runApply = async () => {
    if (!canApply || supplierId == null) return;
    if (!window.confirm(t('supply.supplyAgent.logisticsApplyConfirm'))) return;
    setApplying(true);
    setError(null);
    setApplyOkMessage(null);
    try {
      // Re-simular para asegurar lines_detail y pintar overlay en Orden Sugerido (SUP-AGENT-006).
      const data = await supplyAgentService.logisticsCalculate({
        mode: 'SIMULATE',
        supplier_id: supplierId,
        warehouse_id: warehouseId,
        load_suggested_order: true,
      });
      setCalcResult(data);
      const simulation = logisticsResultToAgentSimulation(data);
      if (!simulation) {
        setApplyOkMessage(
          t('supply.supplyAgent.logisticsApplyNotPersisted', {
            reason: data.status,
          }),
        );
        return;
      }
      onApplyToSuggestedOrder?.(simulation);
      setApplyOkMessage(
        t('supply.supplyAgent.logisticsApplyToTableOk', {
          lines: data.line_adjustments?.length || 0,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.logisticsApplyError'));
    } finally {
      setApplying(false);
    }
  };

  const productErrors = uploadResult?.products?.errors?.length || 0;
  const ruleErrors = uploadResult?.rules?.errors?.length || 0;
  const containerErrors = uploadResult?.containers?.errors?.length || 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      {!hideTitle && (
        <div className="mb-2 flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5 text-sky-600" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
            {t('supply.supplyAgent.logisticsTitle')}
          </h4>
        </div>
      )}
      <p className="mb-3 text-xs text-slate-500">{t('supply.supplyAgent.logisticsHint')}</p>

      {supplierId == null ? (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-600">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>{t('supply.supplyAgent.logisticsNeedSupplierMaster')}</p>
        </div>
      ) : masterLoading ? (
        <div className="mb-3 rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2 text-xs text-slate-500">
          {t('supply.supplyAgent.logisticsMasterLoading')}
        </div>
      ) : masterStatus?.loaded ? (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs text-emerald-900">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t('supply.supplyAgent.logisticsMasterLoadedTitle')}</p>
            {masterStatus.last_updated ? (
              <p className="mt-0.5 text-emerald-800/80">
                {t('supply.supplyAgent.logisticsMasterUpdatedAt', {
                  date: formatUpdatedAt(masterStatus.last_updated, i18n.language),
                })}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => setShowMasterEditor((v) => !v)}
              className="mt-1.5 inline-flex items-center gap-1 rounded border border-emerald-300 bg-white/70 px-2 py-1 text-[11px] font-medium text-emerald-900 hover:bg-white"
            >
              <Pencil className="h-3 w-3" />
              {showMasterEditor
                ? t('supply.supplyAgent.logisticsMasterHideEditor')
                : t('supply.supplyAgent.logisticsMasterViewEdit')}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">{t('supply.supplyAgent.logisticsMasterEmptyTitle')}</p>
            <p className="mt-0.5">{t('supply.supplyAgent.logisticsMasterEmptyHint')}</p>
          </div>
        </div>
      )}

      {showMasterEditor && supplierId != null && masterStatus?.loaded ? (
        <SupplyAgentLogisticsMasterEditor
          supplierId={supplierId}
          onChanged={() => void refreshMasterStatus(supplierId)}
        />
      ) : null}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.target.checked)}
            className="rounded border-slate-300"
          />
          {t('supply.supplyAgent.logisticsReplaceExisting')}
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => void onPickFile(e.target.files?.[0] || null)}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? t('supply.supplyAgent.logisticsUploading') : t('supply.supplyAgent.logisticsUpload')}
        </button>
        <button
          type="button"
          disabled={!canSimulate || simulating}
          onClick={() => void runSimulate()}
          className="rounded-md bg-sky-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50"
          title={!canSimulate ? t('supply.supplyAgent.logisticsNeedSupplier') : undefined}
        >
          {simulating
            ? t('supply.supplyAgent.logisticsSimulating')
            : t('supply.supplyAgent.logisticsSimulate')}
        </button>
        {!hideApplyToOrder ? (
          <button
            type="button"
            disabled={!canApply || applying}
            onClick={() => void runApply()}
            className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
          >
            {applying ? t('supply.supplyAgent.logisticsApplying') : t('supply.supplyAgent.logisticsApply')}
          </button>
        ) : null}
      </div>

      {error ? <p className="mt-2 text-xs text-rose-600">{error}</p> : null}

      {uploadOkMessage ? (
        <div
          className={`mt-3 flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs ${
            productErrors > 0 || ruleErrors > 0 || containerErrors > 0
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-emerald-200 bg-emerald-50 text-emerald-900'
          }`}
          role="status"
        >
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p className="font-medium">{uploadOkMessage}</p>
        </div>
      ) : null}

      {applyOkMessage ? (
        <div
          className="mt-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs text-emerald-900"
          role="status"
        >
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p className="font-medium">{applyOkMessage}</p>
        </div>
      ) : null}

      {uploadResult ? (
        <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2 text-xs text-slate-700">
          <p className="font-medium text-slate-800">{t('supply.supplyAgent.logisticsUploadSummary')}</p>
          <ul className="mt-1 space-y-0.5">
            <li>
              {t('supply.supplyAgent.logisticsProductsCount', {
                created: uploadResult.products?.created || 0,
                updated: uploadResult.products?.updated || 0,
                errors: productErrors,
              })}
            </li>
            <li>
              {t('supply.supplyAgent.logisticsRulesCount', {
                created: uploadResult.rules?.created || 0,
                updated: uploadResult.rules?.updated || 0,
                errors: ruleErrors,
              })}
            </li>
            <li>
              {t('supply.supplyAgent.logisticsContainersCount', {
                created: uploadResult.containers?.created || 0,
                updated: uploadResult.containers?.updated || 0,
                errors: containerErrors,
              })}
            </li>
          </ul>
          {productErrors > 0 || ruleErrors > 0 || containerErrors > 0 ? (
            <p className="mt-1 text-amber-700">{t('supply.supplyAgent.logisticsUploadHasErrors')}</p>
          ) : null}
        </div>
      ) : null}

      {calcResult ? (
        <div className="mt-3 space-y-2 rounded-md border border-sky-100 bg-sky-50/60 px-2.5 py-2 text-xs text-sky-950">
          <p className="font-medium">
            {t('supply.supplyAgent.logisticsStatus', {
              status: logisticsStatusLabel(t, calcResult.status),
            })}
            {calcResult.applied ? ` · ${t('supply.supplyAgent.logisticsAppliedOk')}` : null}
          </p>
          {ruleSummaryText ? (
            <p className="text-[11px] text-sky-900/80">{ruleSummaryText}</p>
          ) : null}
          {calcResult.detail ? (
            <p className="text-amber-800">{localizeLogisticsTechnicalText(t, calcResult.detail)}</p>
          ) : null}
          <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
            <div>
              <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.logisticsLinesChanged')}</p>
              <p>{calcResult.line_adjustments?.length || 0}</p>
            </div>
            {isContainerMode ? (
              <>
                <div>
                  <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.before')}</p>
                  <p>{formatNum(calcResult.before?.volume_m3 ?? calcResult.utilization?.volume_m3_before)} m³</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.after')}</p>
                  <p>{formatNum(calcResult.after?.volume_m3 ?? calcResult.utilization?.volume_m3_after)} m³</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.before')}</p>
                  <p>{formatNum(calcResult.before?.pallet_equivalent)} pal</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.after')}</p>
                  <p>{formatNum(calcResult.after?.pallet_equivalent)} pal</p>
                </div>
              </>
            )}
            <div>
              <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.logisticsTotalDelta')}</p>
              <p>
                {(calcResult.line_adjustments?.length || 0) > 0 ? (
                  <>
                    {totalDeltaUnits > 0 ? '+' : ''}
                    {formatNum(totalDeltaUnits, 0)} uds
                  </>
                ) : (
                  '—'
                )}
              </p>
            </div>
            {isContainerMode ? (
              <div>
                <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.logisticsFillVol')}</p>
                <p>{formatNum(calcResult.utilization?.volume_fill_pct_after, 1)}%</p>
              </div>
            ) : (
              <div>
                <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.logisticsFillPct')}</p>
                <p>{formatNum(calcResult.utilization?.pallet_fill_pct_after, 1)}%</p>
              </div>
            )}
            {!isContainerMode && calcResult.utilization?.volume_fill_pct_after != null ? (
              <div>
                <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.logisticsFillVol')}</p>
                <p>{formatNum(calcResult.utilization.volume_fill_pct_after, 1)}%</p>
              </div>
            ) : null}
            {calcResult.utilization?.weight_fill_pct_after != null ? (
              <div>
                <p className="text-[10px] uppercase text-sky-700/80">{t('supply.supplyAgent.logisticsFillWeight')}</p>
                <p>{formatNum(calcResult.utilization.weight_fill_pct_after, 1)}%</p>
              </div>
            ) : null}
          </div>
          {(calcResult.warnings?.length || 0) > 0 ? (
            <ul className="list-disc pl-4 text-amber-800">
              {calcResult.warnings!.slice(0, 4).map((w) => (
                <li key={w}>{localizeLogisticsTechnicalText(t, w)}</li>
              ))}
            </ul>
          ) : null}
          {(calcResult.missing_data?.length || 0) > 0 &&
          calcResult.status !== 'BLOCKED_MISSING_DATA' ? (
            <p className="text-amber-800">
              {t('supply.supplyAgent.logisticsSkippedPackaging', {
                skipped: calcResult.skipped_lines_count ?? calcResult.missing_data!.length,
                eligible: calcResult.eligible_lines_count ?? '—',
              })}
            </p>
          ) : null}
          {(calcResult.missing_data?.length || 0) > 0 &&
          calcResult.status === 'BLOCKED_MISSING_DATA' &&
          !calcResult.detail ? (
            <p className="text-rose-700">
              {t('supply.supplyAgent.logisticsMissingData', {
                count: calcResult.missing_data!.length,
              })}
            </p>
          ) : null}
          {(calcResult.line_adjustments?.length || 0) > 0 ? (
            <div className="max-h-64 overflow-auto rounded border border-sky-100 bg-white">
              <table className="min-w-full text-[11px]">
                <thead className="bg-sky-50 text-left text-sky-800">
                  <tr>
                    <th className="px-2 py-1">{t('supply.supplyAgent.logisticsColSku')}</th>
                    <th className="px-2 py-1">{t('supply.supplyAgent.logisticsColFamily')}</th>
                    <th className="px-2 py-1">{t('supply.supplyAgent.logisticsColZone')}</th>
                    <th className="px-2 py-1">{t('supply.supplyAgent.qtyBefore')}</th>
                    <th className="px-2 py-1">{t('supply.supplyAgent.qtyAfter')}</th>
                    <th className="px-2 py-1">Δ</th>
                    <th className="min-w-[12rem] px-2 py-1">{t('supply.supplyAgent.logisticsColReason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {calcResult.line_adjustments!.slice(0, 30).map((row) => {
                    const items =
                      row.explanation_items && row.explanation_items.length > 0
                        ? row.explanation_items.map((item) =>
                            localizeLogisticsTechnicalText(t, item),
                          )
                        : null;
                    const fallback = localizeLogisticsTechnicalText(
                      t,
                      row.explanation || row.reason || '—',
                    );
                    const name = row.product_name || '';
                    const family = row.pallet_family || '—';
                    const zoneRaw = row.temperature_zone || '';
                    const zone = zoneRaw
                      ? logisticsZoneLabel(t, zoneRaw) || zoneRaw
                      : '—';
                    const occBefore =
                      row.occupancy_pct_before != null
                        ? `${formatNum(row.occupancy_pct_before, 1)}%`
                        : null;
                    const occAfter =
                      row.occupancy_pct_after != null
                        ? `${formatNum(row.occupancy_pct_after, 1)}%`
                        : null;
                    return (
                      <tr key={row.product_id} className="border-t border-slate-100 align-top">
                        <td className="px-2 py-1">
                          <div className="min-w-0">
                            <div className="font-mono text-[11px] font-medium text-slate-900">
                              {row.sku || row.product_id}
                            </div>
                            {name ? (
                              <div className="truncate text-[10px] text-slate-500" title={name}>
                                {name}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className="max-w-[6rem] truncate px-2 py-1 text-slate-600" title={family}>
                          {family}
                        </td>
                        <td className="px-2 py-1 text-slate-600">{zone}</td>
                        <td className="px-2 py-1">
                          <div>{formatNum(row.old_units, 0)}</div>
                          {occBefore ? (
                            <div
                              className="text-[10px] text-slate-500"
                              title={t('supply.supplyAgent.logisticsOccupancyHint')}
                            >
                              {occBefore}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-2 py-1">
                          <div>{formatNum(row.new_units, 0)}</div>
                          {occAfter ? (
                            <div
                              className="text-[10px] text-slate-500"
                              title={t('supply.supplyAgent.logisticsOccupancyHint')}
                            >
                              {occAfter}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-2 py-1">{formatNum(row.delta_units, 0)}</td>
                        <td className="max-w-[18rem] px-2 py-1 text-slate-600" title={fallback}>
                          {items ? (
                            <ul className="list-disc space-y-0.5 pl-3.5">
                              {items.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            fallback
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
          {(calcResult.alternatives?.length || 0) > 0 ? (
            <div className="rounded border border-sky-100 bg-white px-2 py-1.5">
              <p className="font-medium text-sky-900">{t('supply.supplyAgent.logisticsAlternatives')}</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-slate-700">
                {calcResult.alternatives!.slice(0, 3).map((alt) => {
                  const strategyLabel = logisticsStrategyLabel(t, alt.strategy);
                  const deltaUnits = (alt.line_adjustments || []).reduce(
                    (sum, row) => sum + Number(row.delta_units || 0),
                    0,
                  );
                  return (
                    <li key={alt.strategy}>
                      {t('supply.supplyAgent.logisticsAlternativeItem', {
                        strategy: strategyLabel,
                        lines: alt.line_adjustments?.length || 0,
                      })}
                      {deltaUnits
                        ? `, Δ uds=${Number(deltaUnits).toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}`
                        : ''}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
